import React, { useState, useMemo } from 'react'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { formatFirebaseTimestamp } from '../../utils/datetime';
import HandymanIcon from '@mui/icons-material/Handyman';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
 import SendIcon from '@mui/icons-material/Send';
 import SendMessageModal from '../components/SendMessageModal';
import UserDetailModal from '../components/UserDetailModal';
import { createNotification } from '../../api/UserAPI';

export default function Users({ users }) {
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [onboardingFilter, setOnboardingFilter] = useState('all'); // 'all' | 'completed' | 'not-completed'
     const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
    const [detailUser, setDetailUser] = useState(null);

    const getUserDisplayName = (user) => {
        if (user?.firstName && user?.surname) {
            return `${user.firstName} ${user.surname}`;
        }
        if (user?.name) {
            return user.name;
        }
        return '–';
    };

    // Filter users based on search and onboarding filter
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            // Search filter (case-insensitive)
            const query = searchQuery.toLowerCase().trim();
            const matchesSearch = query === '' || 
                (user?.id?.toLowerCase().includes(query)) ||
                (getUserDisplayName(user).toLowerCase().includes(query));

            // Onboarding filter
            let matchesOnboarding = true;
            if (onboardingFilter === 'completed') {
                matchesOnboarding = user?.onboardingCompleted === true;
            } else if (onboardingFilter === 'not-completed') {
                matchesOnboarding = user?.onboardingCompleted !== true;
            }

            return matchesSearch && matchesOnboarding;
        });
    }, [users, searchQuery, onboardingFilter]);

    // Sort filtered users
    const sortedUserData = useMemo(() => {
        return [...filteredUsers].sort((a, b) => {
            const dateA = new Date(a.createdAt.toDate());
            const dateB = new Date(b.createdAt.toDate());
            return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }, [filteredUsers, sortOrder]);

    const toggleSortOrder = () => {
        setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const cycleOnboardingFilter = () => {
        setOnboardingFilter(prev => {
            if (prev === 'all') return 'completed';
            if (prev === 'completed') return 'not-completed';
            return 'all';
        });
    };

    const getOnboardingFilterIcon = () => {
        if (onboardingFilter === 'completed') {
            return { color: '#22c55e', opacity: 1 };
        }
        if (onboardingFilter === 'not-completed') {
            return { color: '#ef4444', opacity: 1 };
        }
        return { color: '#6c757d', opacity: 0.6 };
    };

    // Selection handlers
    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const allFilteredIds = sortedUserData.map(user => user.id);
        const allSelected = allFilteredIds.every(id => selectedUsers.has(id));
        
        if (allSelected) {
            // Deselect all filtered users
            setSelectedUsers(prev => {
                const newSet = new Set(prev);
                allFilteredIds.forEach(id => newSet.delete(id));
                return newSet;
            });
        } else {
            // Select all filtered users
            setSelectedUsers(prev => {
                const newSet = new Set(prev);
                allFilteredIds.forEach(id => newSet.add(id));
                return newSet;
            });
        }
    };

    const handleClearSelection = () => {
        setSelectedUsers(new Set());
    };
 
     const handleOpenMessageModal = () => {
         setIsMessageModalOpen(true);
     };
 
     const handleCloseMessageModal = () => {
         setIsMessageModalOpen(false);
     };
 
     const handleSendMessage = async (message) => {
         const notification = {
             from: {
                id:'OXjNF7zn8BMqAxpYxnG361T3xoY2',
                name:"Newro"
             }, // Replace with actual current user ID
             to: Array.from(selectedUsers),
             type: 'global',
             message: message
         };
         
         console.log('Sending notification:', notification);

         await createNotification(notification)
         // TODO: Call API to send notification
         // await sendNotification(notification);
         
         handleCloseMessageModal();
         handleClearSelection();
     };

    const getSelectAllState = () => {
        if (sortedUserData.length === 0) return 'none';
        const allFilteredIds = sortedUserData.map(user => user.id);
        const selectedCount = allFilteredIds.filter(id => selectedUsers.has(id)).length;
        if (selectedCount === 0) return 'none';
        if (selectedCount === allFilteredIds.length) return 'all';
        return 'some';
    };

    const selectAllState = getSelectAllState();
    const filterIconStyle = getOnboardingFilterIcon();

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Search Bar */}
            <div className="users-search-container">
                <SearchIcon sx={{ color: '#6c757d', fontSize: '20px' }} />
                <input
                    type="text"
                    placeholder="Search by ID or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="users-search-input"
                />
                {searchQuery && (
                    <ClearIcon 
                        sx={{ color: '#adb5bd', fontSize: '18px', cursor: 'pointer', '&:hover': { color: '#6c757d' } }}
                        onClick={() => setSearchQuery('')}
                    />
                )}
            </div>

            {/* Selection Toolbar */}
            {selectedUsers.size > 0 && (
                <div className="users-selection-toolbar">
                    <span className="selection-count">
                        {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                    </span>
                    <button className="clear-selection-btn" onClick={handleClearSelection}>
                        <ClearIcon sx={{ fontSize: '16px' }} />
                        Clear selection
                    </button>
                     <button className="send-message-btn" onClick={handleOpenMessageModal}>
                         <SendIcon sx={{ fontSize: '16px' }} />
                         Send Message
                     </button>
                </div>
            )}

            {/* Users Table */}
            <table className="request-table">
                <thead>
                    <tr>
                        <th style={{ width: '44px', textAlign: 'center' }}>
                            <div 
                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                                onClick={handleSelectAll}
                            >
                                {selectAllState === 'all' && (
                                    <CheckBoxIcon sx={{ fontSize: '20px', color: 'var(--color-primary)' }} />
                                )}
                                {selectAllState === 'some' && (
                                    <IndeterminateCheckBoxIcon sx={{ fontSize: '20px', color: 'var(--color-primary)' }} />
                                )}
                                {selectAllState === 'none' && (
                                    <CheckBoxOutlineBlankIcon sx={{ fontSize: '20px', color: '#adb5bd' }} />
                                )}
                            </div>
                        </th>
                        <th style={{ width: '44px', textAlign: 'center' }}>
                            <div 
                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                                onClick={cycleOnboardingFilter}
                                title={
                                    onboardingFilter === 'all' ? 'Showing all users' :
                                    onboardingFilter === 'completed' ? 'Showing onboarded users only' :
                                    'Showing non-onboarded users only'
                                }
                            >
                                <PhoneIphoneIcon sx={{ 
                                    fontSize: '16px', 
                                    color: filterIconStyle.color,
                                    opacity: filterIconStyle.opacity,
                                    transition: 'all 0.2s ease'
                                }} />
                            </div>
                        </th>
                        <th style={{ width: '280px' }}>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>
                            <div className="sort-control" onClick={() => toggleSortOrder()}>
                                <div className="sort-arrows">
                                    <ArrowDropUpIcon sx={{ color: sortOrder === "asc" ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(4px)' }} />
                                    <ArrowDropDownIcon sx={{ color: sortOrder === "desc" ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(-4px)' }} />
                                </div>
                                Date Created
                            </div>
                        </th>
                        <th style={{ textAlign: 'right', width: '100px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedUserData.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="empty-state">
                                {searchQuery || onboardingFilter !== 'all' ? 'No users match your filters' : 'No users found'}
                            </td>
                        </tr>
                    ) : sortedUserData.map((user, index) => (
                        <tr
                            key={index}
                            className={`request-row ${selectedUsers.has(user.id) ? 'selected-row' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setDetailUser(user)}
                        >
                            <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <div
                                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                                    onClick={() => handleSelectUser(user.id)}
                                >
                                    {selectedUsers.has(user.id) ? (
                                        <CheckBoxIcon sx={{ fontSize: '20px', color: 'var(--color-primary)' }} />
                                    ) : (
                                        <CheckBoxOutlineBlankIcon sx={{ fontSize: '20px', color: '#adb5bd' }} />
                                    )}
                                </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                                {user?.onboardingCompleted === true && (
                                    <CheckCircleIcon sx={{ fontSize: '16px', color: '#22c55e' }} />
                                )}
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6c757d' }}>
                                {user?.id}
                            </td>
                            <td>
                                <span className="user-name-cell">
                                    <span style={{ fontWeight: 500, color: '#1a1a2e' }}>
                                        {getUserDisplayName(user)}
                                    </span>
                                    {user?.isTrainer === true && (
                                        <FitnessCenterIcon 
                                            sx={{ 
                                                fontSize: '14px', 
                                                color: 'var(--color-primary)', 
                                                marginLeft: '6px',
                                                verticalAlign: 'middle',
                                                opacity: 0.8
                                            }} 
                                        />
                                    )}
                                </span>
                            </td>
                            <td>{user?.email}</td>
                            <td>{formatFirebaseTimestamp(user?.createdAt)}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                                <div className="table-actions">
                                    <HandymanIcon 
                                        onDoubleClick={() => { }} 
                                        sx={{ color: '#adb5bd', cursor: 'pointer', fontSize: '20px', '&:hover': { color: 'var(--color-primary)' } }} 
                                    />
                                    <DeleteForeverIcon 
                                        className="delete-icon"
                                        onDoubleClick={() => { }} 
                                        sx={{ color: '#adb5bd', cursor: 'pointer', fontSize: '20px', '&:hover': { color: '#dc3545' } }} 
                                    />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
             
             <SendMessageModal
                 isOpen={isMessageModalOpen}
                 selectedUsers={Array.from(selectedUsers)}
                 onSend={handleSendMessage}
                 onClose={handleCloseMessageModal}
             />

             {detailUser && (
                 <UserDetailModal
                     user={detailUser}
                     onClose={() => setDetailUser(null)}
                 />
             )}
        </div>
    )
}
